import { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const SITE_URL = isLocal ? window.location.origin : 'https://campervanrentalturkey.com';

// Pages that should NOT be indexed by search engines
const NOINDEX_PATTERNS = [
  '/admin',
  '/owner',
  '/demo',
  '/login',
  '/dashboard',
  '/cart',
  '/reset-password',
  '/thank-you',
  '/payment-callback',
  '/payment-process',
  '/complete-payment',
];

/**
 * CanonicalTag component using react-helmet-async + direct DOM manipulation
 * - Removes query parameters from canonical URL (they create duplicates)
 * - Removes trailing slashes
 * - Uses BOTH Helmet AND direct DOM for maximum crawler compatibility
 */
const CanonicalTag = () => {
  const location = useLocation();

  const currentPath = location.pathname.toLowerCase();

  // Check if this is a noindex page
  const shouldNoIndex = NOINDEX_PATTERNS.some(pattern =>
    currentPath.startsWith(pattern)
  );

  // Build canonical URL for indexable pages - ALWAYS without query params
  let canonicalPath = currentPath;

  // Normalize: remove duplicate slashes
  canonicalPath = canonicalPath.replace(/\/+/g, '/');

  // Remove index.html or index.php
  canonicalPath = canonicalPath.replace(/\/index\.(html|php)$/, '/');

  // Remove trailing slash (except for root)
  if (canonicalPath !== '/' && canonicalPath.endsWith('/')) {
    canonicalPath = canonicalPath.slice(0, -1);
  }

  // Ensure path starts with /
  if (!canonicalPath.startsWith('/')) {
    canonicalPath = '/' + canonicalPath;
  }

  // Build canonical URL - NEVER include query params
  const canonicalUrl = canonicalPath === '/'
    ? SITE_URL
    : `${SITE_URL}${canonicalPath}`;

  // Direct DOM manipulation with persistence to fight against removal
  useLayoutEffect(() => {
    // Debug log - warn to make it visible
    console.warn('CanonicalTag: Initializing', { canonicalUrl, shouldNoIndex });

    const updateCanonical = () => {
      // 1. Clean up existing canonical tags to prevent duplicates
      const existingTags = document.querySelectorAll('link[rel="canonical"]');
      let foundCorrect = false;

      existingTags.forEach(tag => {
        if (tag.getAttribute('href') === canonicalUrl && !foundCorrect) {
          foundCorrect = true;
          // Keep this one
        } else {
          console.warn('CanonicalTag: Removing incorrect/duplicate tag', tag.getAttribute('href'));
          tag.remove();
        }
      });

      if (shouldNoIndex) {
        if (foundCorrect) {
          // We found one but we are noindex, so remove it
          const tag = document.querySelector('link[rel="canonical"]');
          if (tag) tag.remove();
        }
        return;
      }

      if (!foundCorrect) {
        console.warn('CanonicalTag: Injecting tag', canonicalUrl);
        const link = document.createElement('link');
        link.rel = 'canonical';
        link.href = canonicalUrl;
        document.head.appendChild(link);
      }
    };

    // Initial run
    updateCanonical();

    // 2. Observe changes to head to prevent removal
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if our canonical tag was removed
          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLLinkElement && node.rel === 'canonical') {
              console.warn('CanonicalTag: Detected removal of canonical tag!', node.href);
              needsUpdate = true;
            }
          });
          // Check if a new (potentially wrong) canonical tag was added
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLLinkElement && node.rel === 'canonical') {
              if (node.getAttribute('href') !== canonicalUrl) {
                console.warn('CanonicalTag: Detected addition of wrong canonical tag!', node.getAttribute('href'));
                needsUpdate = true;
              }
            }
          });
        }
      });

      if (needsUpdate) {
        console.warn('CanonicalTag: Re-applying canonical tag due to external change');
        updateCanonical();
      }
    });

    observer.observe(document.head, { childList: true, subtree: false });

    return () => {
      observer.disconnect();
    };
  }, [canonicalUrl, shouldNoIndex]);

  // Also use Helmet for SSR/prerender compatibility
  if (shouldNoIndex) {
    return (
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  return null; // Helmet handling removed for canonical to avoid duplicates
};

export default CanonicalTag;
